'use client'

import React from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { motion } from 'framer-motion'
import { AnimatedNumber } from '@/components/ui/animated-number'
import {
  CheckmarkFilled as CheckCircle2,
  ArrowRight,
  Calendar,
  Group as Users,
  Security as Shield,
  Lightning as Zap,
  Target,
  Settings,
  DataBase as Database,
  Education as GraduationCap,
  Rocket,
  Partnership as HeartHandshake,
  Document as FileText,
  Search,
  Tools as Wrench,
  Upload,
  Chemistry as TestTube,
  Locked as Lock,
  Book as BookOpen,
  Play,
  Script as FileCode,
  Headphones,
  ChartLine as LineChart,
  Renew as RefreshCw,
  Time as Clock,
  CheckboxChecked as CheckSquare,
  WarningFilled as AlertCircle,
  Trophy,
  Phone,
  Email as Mail,
  Chat as MessageSquare,
} from '@carbon/icons-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

export default function ImplementationPage() {
  return (
    <>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-background text-foreground">
        <div className="container relative py-16 lg:py-24 z-10">
          <div className="mx-auto max-w-7xl">
            <div className="text-center space-y-8">
              <div className="space-y-6">
                <p className="text-sm font-medium uppercase tracking-wider text-emerald-600 dark:text-primary-active">
                  IMPLEMENTATION EXCELLENCE
                </p>
                <h1 className="font-heading text-5xl font-semibold tracking-tight lg:text-6xl xl:text-7xl">
                  Seamless Implementation:
                  <span className="text-emerald-600 dark:text-primary-active block mt-2">
                    From Planning to Success
                  </span>
                </h1>
                <p className="text-lg leading-relaxed text-muted-foreground lg:text-xl max-w-3xl mx-auto">
                  Transform your institution with our proven 4-week
                  implementation methodology. From initial setup to full
                  deployment, we ensure every step is optimized for your
                  success.
                </p>
              </div>
              <div className="flex flex-wrap gap-4 justify-center">
                <Button size="lg" className="font-medium">
                  Schedule Implementation Kickoff
                  <ArrowRight className="ml-2 size-4" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-border bg-transparent text-foreground hover:bg-accent hover:text-accent-foreground"
                >
                  <Phone className="mr-2 size-4" />
                  Speak with Expert
                </Button>
              </div>

              {/* Success Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
                {[
                  { value: '500+', label: 'Successful Implementations' },
                  { value: '4 Weeks', label: 'Average Setup Time' },
                  { value: '99.9%', label: 'Uptime Guarantee' },
                  // Was "24/7 Support Available", which /support directly
                  // contradicts by publishing Monday-Friday, 9:00-6:00 IST.
                  // The module count is verifiable against @content/schoolerp.
                  { value: '23', label: 'Integrated Modules' },
                ].map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-3xl md:text-4xl font-bold text-emerald-600 dark:text-primary-active">
                      <AnimatedNumber value={stat.value} delay={index * 70} />
                    </div>
                    <div className="text-sm text-muted-foreground mt-2">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4-Week Implementation Process */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <Badge className="mb-4">Structured Approach</Badge>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-4 text-foreground">
                4-Week Implementation Process
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Our proven methodology ensures a smooth transition with minimal
                disruption to your operations
              </p>
            </motion.div>

            <Tabs defaultValue="week1" className="mt-12">
              <TabsList className="grid w-full grid-cols-4 gap-1 sm:gap-2 p-1 h-auto">
                <TabsTrigger
                  value="week1"
                  className="text-[10px] sm:text-sm px-1 sm:px-3 py-2 sm:py-2 h-auto min-h-[40px] sm:min-h-[44px]"
                >
                  Week 1
                </TabsTrigger>
                <TabsTrigger
                  value="week2"
                  className="text-[10px] sm:text-sm px-1 sm:px-3 py-2 sm:py-2 h-auto min-h-[40px] sm:min-h-[44px]"
                >
                  Week 2
                </TabsTrigger>
                <TabsTrigger
                  value="week3"
                  className="text-[10px] sm:text-sm px-1 sm:px-3 py-2 sm:py-2 h-auto min-h-[40px] sm:min-h-[44px]"
                >
                  Week 3
                </TabsTrigger>
                <TabsTrigger
                  value="week4"
                  className="text-[10px] sm:text-sm px-1 sm:px-3 py-2 sm:py-2 h-auto min-h-[40px] sm:min-h-[44px]"
                >
                  Week 4
                </TabsTrigger>
              </TabsList>

              <TabsContent value="week1">
                <Card className="p-4 sm:p-6 lg:p-8 border-2 hover:border-emerald-600/30 dark:hover:border-primary-active/30 transition-all duration-300">
                  <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-emerald-600/10 dark:bg-primary-active/10 flex items-center justify-center flex-shrink-0">
                      <Search className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600 dark:text-primary-active" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-foreground">
                        Week 1: Discovery & Planning
                      </h3>
                      <p className="text-sm sm:text-base text-muted-foreground mb-4 sm:mb-6">
                        Laying the foundation for a successful implementation
                        through comprehensive analysis and strategic planning.
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                        {[
                          {
                            icon: FileText,
                            title: 'Requirements Analysis',
                            desc: 'Deep dive into your institutional needs and objectives',
                          },
                          {
                            icon: Settings,
                            title: 'System Assessment',
                            desc: 'Evaluate current infrastructure and integration points',
                          },
                          {
                            icon: Target,
                            title: 'Configuration Planning',
                            desc: 'Design optimal system configuration for your workflow',
                          },
                          {
                            icon: Database,
                            title: 'Data Strategy',
                            desc: 'Map data migration paths and establish governance',
                          },
                        ].map((item, idx) => (
                          <div key={idx} className="flex gap-3">
                            <item.icon className="w-5 h-5 text-emerald-600 dark:text-primary-active mt-1 flex-shrink-0" />
                            <div>
                              <h4 className="font-semibold mb-1 text-foreground">
                                {item.title}
                              </h4>
                              <p className="text-sm text-muted-foreground">
                                {item.desc}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </Card>
              </TabsContent>

              <TabsContent value="week2">
                <Card className="p-4 sm:p-6 lg:p-8 border-2 hover:border-emerald-600/30 dark:hover:border-primary-active/30 transition-all duration-300">
                  <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-emerald-600/10 dark:bg-primary-active/10 flex items-center justify-center flex-shrink-0">
                      <Wrench className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600 dark:text-primary-active" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-foreground">
                        Week 2: Setup & Configuration
                      </h3>
                      <p className="text-sm sm:text-base text-muted-foreground mb-4 sm:mb-6">
                        Building your customized system with precision and
                        attention to detail.
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                        {[
                          {
                            icon: Settings,
                            title: 'System Installation',
                            desc: 'Deploy core modules and establish system architecture',
                          },
                          {
                            icon: Zap,
                            title: 'Module Configuration',
                            desc: 'Customize features to match your institutional processes',
                          },
                          {
                            icon: Users,
                            title: 'User Setup',
                            desc: 'Create user accounts and establish permission hierarchies',
                          },
                          {
                            icon: RefreshCw,
                            title: 'Integration',
                            desc: 'Connect with existing systems and third-party services',
                          },
                        ].map((item, idx) => (
                          <div key={idx} className="flex gap-3">
                            <item.icon className="w-5 h-5 text-emerald-600 dark:text-primary-active mt-1 flex-shrink-0" />
                            <div>
                              <h4 className="font-semibold mb-1 text-foreground">
                                {item.title}
                              </h4>
                              <p className="text-sm text-muted-foreground">
                                {item.desc}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </Card>
              </TabsContent>

              <TabsContent value="week3">
                <Card className="p-4 sm:p-6 lg:p-8 border-2 hover:border-emerald-600/30 dark:hover:border-primary-active/30 transition-all duration-300">
                  <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-emerald-600/10 dark:bg-primary-active/10 flex items-center justify-center flex-shrink-0">
                      <Upload className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600 dark:text-primary-active" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-foreground">
                        Week 3: Migration & Testing
                      </h3>
                      <p className="text-sm sm:text-base text-muted-foreground mb-4 sm:mb-6">
                        Ensuring data integrity and system reliability through
                        rigorous testing protocols.
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                        {[
                          {
                            icon: Database,
                            title: 'Data Migration',
                            desc: 'Secure transfer of historical data with validation checks',
                          },
                          {
                            icon: TestTube,
                            title: 'Testing',
                            desc: 'Comprehensive testing of all modules and workflows',
                          },
                          {
                            icon: LineChart,
                            title: 'Optimization',
                            desc: 'Performance tuning for optimal speed and efficiency',
                          },
                          {
                            icon: Lock,
                            title: 'Security',
                            desc: 'Implementation of security protocols and access controls',
                          },
                        ].map((item, idx) => (
                          <div key={idx} className="flex gap-3">
                            <item.icon className="w-5 h-5 text-emerald-600 dark:text-primary-active mt-1 flex-shrink-0" />
                            <div>
                              <h4 className="font-semibold mb-1 text-foreground">
                                {item.title}
                              </h4>
                              <p className="text-sm text-muted-foreground">
                                {item.desc}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </Card>
              </TabsContent>

              <TabsContent value="week4">
                <Card className="p-4 sm:p-6 lg:p-8 border-2 hover:border-emerald-600/30 dark:hover:border-primary-active/30 transition-all duration-300">
                  <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-emerald-600/10 dark:bg-primary-active/10 flex items-center justify-center flex-shrink-0">
                      <Rocket className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600 dark:text-primary-active" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-foreground">
                        Week 4: Training & Launch
                      </h3>
                      <p className="text-sm sm:text-base text-muted-foreground mb-4 sm:mb-6">
                        Empowering your team and launching with confidence.
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                        {[
                          {
                            icon: GraduationCap,
                            title: 'Training',
                            desc: 'Comprehensive training sessions for all user groups',
                          },
                          {
                            icon: Play,
                            title: 'Go-Live Preparation',
                            desc: 'Final checks and launch readiness assessment',
                          },
                          {
                            icon: Rocket,
                            title: 'System Launch',
                            desc: 'Coordinated system launch with real-time support',
                          },
                          {
                            icon: FileCode,
                            title: 'Documentation',
                            desc: 'Delivery of customized documentation and guides',
                          },
                        ].map((item, idx) => (
                          <div key={idx} className="flex gap-3">
                            <item.icon className="w-5 h-5 text-emerald-600 dark:text-primary-active mt-1 flex-shrink-0" />
                            <div>
                              <h4 className="font-semibold mb-1 text-foreground">
                                {item.title}
                              </h4>
                              <p className="text-sm text-muted-foreground">
                                {item.desc}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </section>

      {/* Post-Implementation Support */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <Badge className="mb-4">Continuous Support</Badge>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-4 text-foreground">
                Post-Implementation Support
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Our commitment doesn't end at launch. We provide comprehensive
                support to ensure your continued success.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: Clock,
                  title: 'First 30 Days',
                  subtitle: 'Hypercare Phase',
                  features: [
                    'Daily system monitoring',
                    'Immediate issue resolution',
                    'Usage analytics and insights',
                    'Performance optimization',
                  ],
                  highlight: true,
                },
                {
                  icon: HeartHandshake,
                  title: '30-90 Days',
                  subtitle: 'Stabilization Phase',
                  features: [
                    'Weekly check-ins',
                    'Additional training sessions',
                    'Feature refinement',
                    'Best practice guidance',
                  ],
                  highlight: false,
                },
                {
                  icon: Trophy,
                  title: 'Ongoing',
                  subtitle: 'Partnership Phase',
                  features: [
                    'Regular updates and upgrades',
                    'Quarterly business reviews',
                    'Strategic consultation',
                    'Priority support access',
                  ],
                  highlight: false,
                },
              ].map((phase, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card
                    className={`p-8 h-full border-2 transition-all duration-300 ${
                      phase.highlight
                        ? 'border-emerald-600/50 dark:border-primary-active/50 bg-emerald-600/5 dark:bg-primary-active/5'
                        : 'hover:border-emerald-600/30 dark:hover:border-primary-active/30'
                    }`}
                  >
                    <div className="flex items-center gap-4 mb-6">
                      <div
                        className={`w-14 h-14 rounded-full flex items-center justify-center ${
                          phase.highlight
                            ? 'bg-emerald-600/20 dark:bg-primary-active/20'
                            : 'bg-muted'
                        }`}
                      >
                        <phase.icon
                          className={`w-7 h-7 ${
                            phase.highlight
                              ? 'text-emerald-600 dark:text-primary-active'
                              : 'text-muted-foreground'
                          }`}
                        />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-foreground">
                          {phase.title}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {phase.subtitle}
                        </p>
                      </div>
                    </div>
                    <ul className="space-y-3">
                      {phase.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-primary-active mt-0.5 flex-shrink-0" />
                          <span className="text-muted-foreground">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Our Implementation */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <Badge className="mb-4">Our Advantage</Badge>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-4 text-foreground">
                Why Choose Our Implementation
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Experience the difference with our proven expertise and
                commitment to excellence
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: Shield,
                  title: 'Risk-Free Guarantee',
                  description:
                    'We guarantee successful implementation or your money back. Our track record speaks for itself.',
                },
                {
                  icon: Users,
                  title: 'Expert Team',
                  description:
                    'Certified professionals with deep domain expertise in educational technology implementations.',
                },
                {
                  icon: Zap,
                  title: 'Rapid Deployment',
                  description:
                    'Go live in just 4 weeks with our streamlined process and pre-built configurations.',
                },
                {
                  icon: HeartHandshake,
                  title: 'White-Glove Service',
                  description:
                    'Dedicated project manager and support team throughout your journey.',
                },
                {
                  icon: Target,
                  title: 'Customized Approach',
                  description:
                    'Tailored implementation strategy that aligns with your institutional goals.',
                },
                {
                  icon: LineChart,
                  title: 'Proven ROI',
                  description:
                    'Average 40% efficiency improvement within the first 6 months post-implementation.',
                },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group"
                >
                  <Card className="p-6 h-full border-2 hover:border-emerald-600/30 dark:hover:border-primary-active/30 hover:shadow-xl transition-all duration-300">
                    <div className="w-12 h-12 rounded-lg bg-emerald-600/10 dark:bg-primary-active/10 flex items-center justify-center mb-4 group-hover:bg-emerald-600/20 dark:group-hover:bg-primary-active/20 transition-colors">
                      <item.icon className="w-6 h-6 text-emerald-600 dark:text-primary-active" />
                    </div>
                    <h3 className="text-xl font-bold mb-3 text-foreground">
                      {item.title}
                    </h3>
                    <p className="text-muted-foreground">{item.description}</p>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Implementation Readiness Checklist */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <Badge className="mb-4">Get Prepared</Badge>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-6 text-foreground">
                  Implementation Readiness Checklist
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  Ensure a smooth implementation by preparing these key items in
                  advance. Our team will guide you through each requirement.
                </p>

                <Accordion type="single" collapsible>
                  {[
                    {
                      title: 'Technical Requirements',
                      items: [
                        'Server specifications and access credentials',
                        'Network configuration details',
                        'SSL certificates and domain information',
                        'Backup and recovery procedures',
                      ],
                    },
                    {
                      title: 'Organizational Data',
                      items: [
                        'Current student and staff databases',
                        'Course catalog and curriculum structure',
                        'Historical academic records',
                        'Financial and accounting data',
                      ],
                    },
                    {
                      title: 'Team Preparation',
                      items: [
                        'Identify key stakeholders and champions',
                        'Designate system administrators',
                        'Schedule training participants',
                        'Prepare change management communication',
                      ],
                    },
                  ].map((section, index) => (
                    <AccordionItem key={index} value={`item-${index}`}>
                      <AccordionTrigger>
                        <span className="text-left">{section.title}</span>
                      </AccordionTrigger>
                      <AccordionContent>
                        <ul className="space-y-2">
                          {section.items.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-primary-active mt-0.5 flex-shrink-0" />
                              <span className="text-sm text-muted-foreground">
                                {item}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="relative"
              >
                <Card className="p-8 bg-card text-card-foreground border-0">
                  <div className="mb-6">
                    <AlertCircle className="w-12 h-12 text-emerald-600 dark:text-primary-active mb-4" />
                    <h3 className="text-2xl font-bold mb-3">
                      Success Guarantee
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      We're so confident in our implementation process that we
                      guarantee your success. If we don't deliver on our
                      promises, we'll work for free until we do.
                    </p>
                  </div>

                  <div className="space-y-4 mb-8">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-emerald-600/20 dark:bg-primary-active/20 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-primary-active" />
                      </div>
                      <span className="text-muted-foreground">
                        On-time delivery guarantee
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-emerald-600/20 dark:bg-primary-active/20 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-primary-active" />
                      </div>
                      <span className="text-muted-foreground">
                        Zero data loss commitment
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-emerald-600/20 dark:bg-primary-active/20 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-primary-active" />
                      </div>
                      <span className="text-muted-foreground">
                        100% user adoption support
                      </span>
                    </div>
                  </div>

                  <div className="pt-6 border-t border-border">
                    <p className="text-sm text-muted-foreground mb-4">
                      Average implementation metrics:
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-2xl font-bold text-emerald-600 dark:text-primary-active">
                          <AnimatedNumber value="28 days" />
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Time to go-live
                        </div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-emerald-600 dark:text-primary-active">
                          <AnimatedNumber value="95%" />
                        </div>
                        <div className="text-xs text-muted-foreground">
                          User satisfaction
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-background relative overflow-hidden">
        <div className="container px-6 lg:px-8 relative z-10">
          <div className="mx-auto max-w-4xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Badge className="mb-4 bg-emerald-600/10 dark:bg-primary-active/10 text-emerald-600 dark:text-primary-active border-emerald-600/30 dark:border-primary-active/30">
                Ready to Transform?
              </Badge>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-6 text-foreground">
                Start Your Implementation Journey Today
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                Join hundreds of institutions that have successfully transformed
                their operations with our platform. Your success story starts
                with a single conversation.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Button size="lg" className="font-medium">
                  Schedule Implementation Kickoff
                  <Calendar className="ml-2 size-4" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-border bg-transparent text-foreground hover:bg-accent hover:text-accent-foreground"
                >
                  Download Implementation Guide
                  <ArrowRight className="ml-2 size-4" />
                </Button>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-muted-foreground">
                <a
                  href="mailto:support@euclideum.com"
                  className="flex items-center gap-2 hover:text-emerald-600 dark:hover:text-primary-active transition-colors"
                >
                  <Mail className="w-5 h-5" />
                  <span>support@euclideum.com</span>
                </a>
                <a
                  href="tel:+918799874471"
                  className="flex items-center gap-2 hover:text-emerald-600 dark:hover:text-primary-active transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  <span>+91 87998 74471</span>
                </a>
                <button className="flex items-center gap-2 hover:text-emerald-600 dark:hover:text-primary-active transition-colors">
                  <MessageSquare className="w-5 h-5" />
                  <span>Live Chat</span>
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  )
}
